import { createUserWithEmailAndPassword, getAuth, sendEmailVerification, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import React, { useContext, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createProfile, editProfile, getProfile, updateProfile } from "../firebase/controller/profile";
import { auth, db } from "../firebase/firebase";
import { setLoginUser, setProfile } from "../redux/slices/auth";
import { uploadMultiple } from "../utils";
const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // const search = useLocation().search;
  const [user, setUser] = useState();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [timeActive, setTimeActive] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const dispatch = useDispatch();
  const [isLogin, setIsLogin] = useState(false);
  const [isPictureUpdated, setIsPictureUpdated] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [mediaURLs, setMediaURLs] = useState([]);
  const [done, setDone] = useState(false);
  const [values, setValues] = useState();

  const { user: savedUser } = useSelector((state) => state.authStore);

  const signup = async (values) => {
    try {
      setLoading(true);
      // const ref = new URLSearchParams(search).get('ref');
      const { user } = await createUserWithEmailAndPassword(auth, values.email, values.password);
      const userRef = doc(db, `users`, user.uid);
      await setDoc(userRef, {
        userID: user.uid,
        isProfileCompleted: false,
        createdAt: new Date(),
      });
      await createProfile({ user, values });
      await sendEmailVerification(user);
      setTimeActive(true);
      toast.success("Account created!, Check your Email to verify your account");
      navigate("/verification");
      setLoading(false);
    } catch (err) {
      if (err.code === "auth/network-request-failed") {
        toast.error("A network error has occurred, Check your network router or perhaps try again later");
      } else if (err.code === "auth/email-already-in-use") {
        toast.error(`${values.email} is already in use by another account.`);
      } else if (err.code === "auth/invalid-email") {
        toast.error("The email address is badly formatted.");
      } else {
        toast.error("There is an error creating user", err);
      }
      setLoading(false);
    }
  };

  async function login(values) {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, values.email, values.password);
      setIsLogin(true);
      if (!auth.currentUser.emailVerified) {
        navigate("/verification");
        toast.success("User is unverified!, A verification link has been sent to your email");
      }
    } catch (err) {
      if (err.code === "auth/wrong-password") {
        toast.error("Incorrect password");
      } else if (err.code === "auth/user-not-found") {
        toast.error("Email does not exist");
      } else {
        toast.error(err.message);
      }
      setLoading(false);
    }
  }

  async function logout() {
    try {
      if (auth.currentUser || (savedUser && !auth.currentUser)) {
        dispatch({ type: "logout" });
        setIsLogin(false);
        await signOut(auth);
        navigate("/");
      }
    } catch (error) {
      const message = error.message;
      toast.error(message);
    }
  }

  const resendVerificationMail = async () => {
    try {
      if (auth.currentUser) {
        setButtonDisabled(true);
        await sendEmailVerification(auth.currentUser);
        toast.success("Email successfully sent");
        setTimeActive(true);
        setButtonDisabled(false);
      } else {
        toast.error("Only loggedin users can send verification mail");
      }
    } catch (error) {
      const message = error.message;
      toast.error(message);
      setButtonDisabled(false);
    }
  };

  const updateUserProfile = useCallback(
    async (status, edit) => {
      if (uploadImage) {
        uploadMultiple(uploadImage, mediaURLs, setMediaURLs, setLoading, status, async () => {
          if (edit) {
            const res = await editProfile(values, isPictureUpdated);
            res && toast.success("Profile updated successfully");
          } else {
            const res = await updateProfile(values, mediaURLs);
            res && toast.success("Profile updated successfully.");
          }
          setRefetch((prev) => !prev);
          setLoading(false);
          setDone(true);
        });
      } else {
        try {
          setLoading(true);
          if (edit) {
            const res = await editProfile(values, isPictureUpdated);
            res && toast.success("Profile updated successfully");
          } else {
            const res = await updateProfile(values);
            res && toast.success("Profile updated successfully.");
          }
          setRefetch((prev) => !prev);
          setLoading(false);
          setDone(true);
        } catch (err) {
          if (err.code === "auth/requires-recent-login") {
            toast.error("Action to update email/password requires recent login, Please login again");
          } else if (err.code === "auth/email-already-in-use") {
            toast.error(`${values.email} is already in use by another account.`);
          } else {
            toast.error("There is an error updating user profile, try again");
          }
          console.log(err.message);
          setLoading(false);
          setDone(true);
        }
      }
    },
    [uploadImage, mediaURLs, values, isPictureUpdated]
  );

  
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        getDoc(doc(db, "users", user.uid)).then((doc) => {
          dispatch(setLoginUser({ ...user, isProfileCompleted: doc.data().isProfileCompleted }));
          getProfile(user).then((res) => {
            dispatch(setProfile(res?.profile));
            
            setLoading(false);
            if (isLogin && user.emailVerified) {
              navigate("/feed");
            }
          });
        });
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, refetch]);

  useEffect(() => {
    (async function () {
      if (uploadImage && mediaURLs.length === Array.from(uploadImage).length) {
        await updateUserProfile(true, false);
      }
    })();
  }, [updateUserProfile, mediaURLs.length, uploadImage]);

  const value = {
    user,
    login,
    signup,
    logout,
    loading,
    setLoading,
    resendVerificationMail,
    buttonDisabled,
    timeActive,
    setTimeActive,
    updateUserProfile,
    setIsPictureUpdated,
    setUploadImage,
    done,
    setValues,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
